/**
 * Track Mobile — the pieces every screen is built from
 * (design_handoff_track_mobile §3, §5.9, §9).
 *
 * Every value here is transcribed from the prototype, not rounded to a scale.
 * §3 rule 1 is explicit: "If the design says 14, it is 14, not 12 or 16."
 *
 * THE ROW RULE (§3 rule 2) is the one that has been got wrong before, so it is
 * worth stating where it lives. A list row carries BOTH padding and a
 * `min-height`, and it is never centred inside a fixed height. When the title
 * wraps, the row grows and the space above the text still equals the space
 * below. Release D: `row()` inside `group()` is the one place rows are built
 * (PR 7 deleted listRow, genericRow and phoneRow with the other Release C
 * helpers once nothing used them).
 */
import { icon, sf } from './icons.js';
import { safeLink } from './phone-model.js';

export const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const ATT = 'var(--m-attention)';
export const OK = 'var(--m-ok)';
export const AC = 'var(--m-accent)';

/** The 7–8px status dot. Status is a dot, never an icon (§3 rule 6). */
export const dot = (colour, size = 7) => `<span style="flex:0 0 ${size}px;width:${size}px;
  height:${size}px;border-radius:999px;background:${colour}"></span>`;

/**
 * Segmented control — Release D (BRIEF §3.5, lib.py segmented): a 32px capsule
 * track on --m-fill, 28px cells, the active one on the thumb with a 3px shadow,
 * 13/600. Every caller of the old .p4-seg gets the iOS control. A count rides in
 * the label; `warn` adds the 6px orange dot (a tab that needs you).
 */
export const segmented = (items, activeKey, actPrefix, { full = true } = {}) => `<div class="p4-sgc${full ? '' : ' fit'}" role="tablist">
  ${items.map((it) => {
    const on = it.key === activeKey;
    return `<button type="button" role="tab" aria-selected="${on}" data-act="${esc(actPrefix)}:${esc(it.key)}"
      class="${on ? 'on' : ''}">${esc(it.label)}${it.count != null && it.count !== '' ? ` ${esc(it.count)}` : ''}${it.warn ? dot(ATT, 6) : ''}</button>`;
  }).join('')}</div>`;

/** The switch (Release D, BRIEF §3.5): 51 × 31, iOS green when on, a 27px white knob (.p4-switch). */
export const toggle = (on, act, label = '') => `<button type="button" data-act="${esc(act)}" class="p4-switch${on ? ' on' : ''}" role="switch" aria-checked="${!!on}"${label ? ` aria-label="${esc(label)}"` : ''}><i aria-hidden="true"></i></button>`;

/** Fire the tick's pop. The only scale animation in the app (§3 rule 10). */
export function popTick(el) {
  if (!el) return;
  el.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }],
    { duration: 240, easing: 'ease-out' },
  );
}

/**
 * Bars and charts grow once, on entry, staggered (§9 motion). They are rendered
 * at zero width and grown on the next frame so the transition actually runs —
 * setting the final width in the same paint would show no animation at all.
 */
export function growBars(root, stagger = 40) {
  const bars = [...root.querySelectorAll('[data-grow]')];
  bars.forEach((b, i) => {
    const target = b.style.width || b.dataset.w || '0%';
    const h = b.dataset.h;
    b.dataset.w = target;
    if (h) b.style.height = '0px'; else b.style.width = '0%';
    setTimeout(() => {
      if (h) b.style.height = h; else b.style.width = target;
    }, 40 + i * stagger);
  });
}

/* ── v4.0.0 primitives (design export p77–p85; docs/ui-refresh/06 §2) ─────────
 * Class-based: the look lives in v4/phone.css, so these only say WHAT a thing
 * is. Every dynamic value is escaped here, once. */

/** Firestore keeps V1's title-case statuses; the pill says them in sentence case. */
export const stageLabel = (status) => {
  const s = String(status || '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
};
/** Loading rows: Line-coloured bars, never a spinner. */
export const skeleton = (n = 3) => Array.from({ length: n }, () => '<div class="p4-skel" aria-hidden="true"></div>').join('');

/* ── The company (v4.1.0; Release D PR 7). The company is drawn from the org
 * document — its logo when it has one (branding.logoPath, resolved once at boot
 * into state.brandLogo), else its initials on the ink fill — never a built-in
 * company. The v4.1.0 appHeader that drew it is gone: a tab root is navLarge. */

/** Letters for a company: "Demo Contractor Ltd" → "DC", "Renov8" → "RE"; a short
 *  one-word name is shown whole ("ASP" → "ASP", Karan 27/10/2026: "The AS is weird"). */
export const companyInitials = (name) => {
  const words = String(name || '').replace(/\b(ltd|limited|llp|plc)\b\.?/gi, '').replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 1 && words[0].length <= 3) return words[0].toUpperCase();
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || 'T').slice(0, 2)).toUpperCase();
};

/** The company's 32px disc (boards Ctr-*: navLarge's leading): the logo fitted inside it, else its
 *  letters on the ink fill. `logo` is an https URL already resolved, or ''. */
export function companyDisc(name, logo) {
  const ok = /^https:\/\//i.test(String(logo || ''));
  return `<span class="p4-codisc${ok ? ' img' : ''}" role="img" aria-label="${esc(name || 'Your company')}">${ok ? `<img src="${esc(logo)}" alt="">` : esc(companyInitials(name))}</span>`;
}

/** The setup seat's line inside a phone screen (setup-seat.js phoneSeat()): the
 *  company it is being set up for and, until the owner has joined, Hand over. */
export const seatLine = (seat) => (!seat ? '' : `<div class="p4-seat" role="status">
  <span class="p4-seat-t">${seat.ownerJoined ? `You’re in ${esc(seat.company)} as Track’s IT admin` : `Setting up ${esc(seat.company)} for its owner`}</span>
  ${seat.ownerJoined ? '' : '<button type="button" class="p4-seat-a" data-act="seat-hand">Hand over</button>'}</div>`);

/* ── Release C (phone review 30/10/2026): the component layer ─────────────────
 * One helper per piece the boards are drawn with (docs/phone-release-c/boards/
 * lib.py); the look is in v4/phone.css. Text arguments are escaped here; the
 * arguments named *Html are safe HTML built by another helper. */

/** progress: a 3px bar and "Step n of N" (P2). */
export const progress = (step, of, label = '') => {
  const pct = of ? Math.max(0, Math.min(100, Math.round((step / of) * 100))) : 0;
  return `<div class="p4-progress" role="progressbar" aria-valuemin="0" aria-valuemax="${esc(of)}" aria-valuenow="${esc(step)}"><div class="p4-progress-t"><i style="width:${pct}%"></i></div><span class="p4-progress-l">${esc(label || `Step ${step} of ${of}`)}</span></div>`;
};

/** question: the step's question is its title (P2), 26/500, a grey hint, an optional crumb above. */
export const question = (q, { hint = '', crumb = '' } = {}) =>
  `<div class="p4-q">${crumb ? `<div class="p4-q-crumb">${esc(crumb)}</div>` : ''}<h1 class="p4-q-h">${esc(q)}</h1>${hint ? `<p class="p4-q-hint">${esc(hint)}</p>` : ''}</div>`;

/**
 * A sheet's header — Release D (BRIEF §3.6, lib.py sheet): 52px, Cancel 17/400 in
 * the tint on the LEFT, the title 17/600 centred, Done/Save 17/600 tint on the
 * right. `sub` is a 12px grey line under the title. `cancel: ''` drops it.
 */
export const sheetHead = (title, { sub = '', cancel = 'Cancel', act = 'close-sheet', done = '', doneAct = '' } = {}) =>
  `<div data-sheet-head class="p4-sheethead"><span class="p4-sheethead-l">${cancel ? `<button type="button" class="p4-navtext" data-act="${esc(act)}">${esc(cancel)}</button>` : ''}</span>
    <div class="p4-sheethead-t"><span>${esc(title)}</span>${sub ? `<small>${esc(sub)}</small>` : ''}</div>
    <span class="p4-sheethead-r">${done ? `<button type="button" class="p4-navtext bold" data-act="${esc(doneAct || act)}">${esc(done)}</button>` : ''}</span></div>`;


/* ══ Release D (docs/phone-release-d/BRIEF.md §3; lib.py is the measured spec) ══
 * The phone is an iOS app (law P10). Every helper below has ONE home here and its
 * look in v4/phone.css (the .p4-* Release D block). Text arguments are escaped
 * here; arguments named *Html are safe HTML built by another helper. Nothing
 * here writes a colour: the one exception is sym(), whose fixed per-destination
 * colours (More, BRIEF §5.A) arrive as data.
 */

/** Track's wordmark tile (28px, ink ground, two rules and the rose node) — Today's leading. */
export const wordmark = () => `<span class="p4-wm" role="img" aria-label="Track"><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 4h10M3 8h10"></path><circle cx="12.5" cy="12" r="1.8"></circle></svg></span>`;

/** A 44px circular glass button for a large-title bar (`tint` fills it with the tint). */
export const glassBtn = (ic, label, { tint = false, act = '' } = {}) =>
  `<button type="button" class="p4-glass${tint ? ' tint' : ''}"${act ? ` data-act="${esc(act)}"` : ''} aria-label="${esc(label)}">${sf(ic, 22, 2)}</button>`;

/** Your 36px avatar in a bar (44 hit): opens You. */
export const avatarBtn = (ini, act = 'team', label = 'You: your profile and settings') =>
  `<button type="button" class="p4-avbtn" data-act="${esc(act)}" aria-label="${esc(label)}">${esc(ini || '·')}</button>`;

/** A nav bar's icon button (44 hit, 24 glyph, tint). */
export const navIconBtn = (ic, label, act) => `<button type="button" class="p4-navicon" data-act="${esc(act)}" aria-label="${esc(label)}">${sf(ic, 24, 2)}</button>`;
/** A nav bar's text button (17, tint; `bold` 600 for Done / Save). */
export const navTextBtn = (label, act, { bold = false } = {}) => `<button type="button" class="p4-navtext${bold ? ' bold' : ''}" data-act="${esc(act)}">${esc(label)}</button>`;

/**
 * navLarge (BRIEF §3.1) — tab roots and section roots. A 44px inline row (the
 * leading, the small title that shows once collapsed, up to two trailing
 * controls), the 34/700 title with its grey count and 15px subtitle, a 1px
 * sentinel the shell watches (past it the screen is .is-collapsed: the large
 * title fades and the bar shows the title centred on glass), then the search.
 */
export function navLarge({ title, subtitle = '', subtitleHtml = '', count = null, leadingHtml = '', trailingHtml = '', searchHtml = '' }) {
  return `<header class="p4-nl">
    <div class="p4-nl-bar"><div class="p4-nl-lead">${leadingHtml}</div>
      <div class="p4-nl-mini" aria-hidden="true">${esc(title)}</div>
      <div class="p4-nl-trail">${trailingHtml}</div></div>
    <div class="p4-nl-title"><h1>${esc(title)}${count != null && count !== '' ? `<span class="p4-nl-count">${esc(count)}</span>` : ''}</h1>${subtitleHtml || subtitle ? `<div class="p4-nl-sub">${subtitleHtml || esc(subtitle)}</div>` : ''}</div>
    <div class="p4-nl-sentinel" data-collapse-sentinel aria-hidden="true"></div>
    ${searchHtml}
  </header>`;
}

/**
 * navInline (BRIEF §3.1) — every pushed screen, flow and full-screen sheet: 44px
 * on a glass band, ‹ Back in the tint (the label is where it goes, P4) or a
 * Cancel, the title 17/600 centred with an optional 12px subtitle, one or two
 * trailing items (safe HTML), or a bold Done.
 */
export function navInline({ back = '', backAct = 'back', title = '', subtitle = '', trailingHtml = '', cancel = '', cancelAct = 'back', done = '', doneAct = '', leadingHtml = '' }) {
  // `leadingHtml` (safe HTML) stands in for Back where there is nowhere to go back to —
  // the desktop at phone width's own bar (mobile-nav.js: the company's disc).
  const left = cancel ? navTextBtn(cancel, cancelAct)
    : back ? `<button type="button" class="p4-navback" data-act="${esc(backAct)}">${sf('chevron-l', 26, 2.2)}<span>${esc(back)}</span></button>` : leadingHtml;
  const right = done ? navTextBtn(done, doneAct, { bold: true }) : trailingHtml;
  return `<header class="p4-ni"><div class="p4-ni-l">${left}</div>
    <div class="p4-ni-t"><div class="t">${esc(title)}</div>${subtitle ? `<div class="s">${esc(subtitle)}</div>` : ''}</div>
    <div class="p4-ni-r">${right}</div></header>`;
}

/**
 * searchField (BRIEF §3.3): a 36px capsule on --m-fill, magnifier left, the Ask
 * sparkle right. `act` makes it a button (Today: opens Ask); otherwise it holds
 * an <input> whose attributes `inputAttrs` gives (safe, built with esc()).
 */
export function searchField(placeholder, { act = '', value = '', trailingIcon = 'sparkle', inputAttrs = '' } = {}) {
  const tail = trailingIcon ? `<i class="p4-sfield-tail" aria-hidden="true">${sf(trailingIcon, 18, 1.8)}</i>` : '';
  if (act) return `<div class="p4-sfield-wrap"><button type="button" class="p4-sfield" data-act="${esc(act)}" aria-label="${esc(placeholder)}">${sf('search', 18, 2.2)}<span class="ph">${esc(value || placeholder)}</span>${tail}</button></div>`;
  return `<div class="p4-sfield-wrap"><label class="p4-sfield">${sf('search', 18, 2.2)}<input type="search" value="${esc(value)}" placeholder="${esc(placeholder)}" aria-label="${esc(placeholder)}" autocomplete="off" enterkeyhint="search" ${inputAttrs}>${tail}</label></div>`;
}

/**
 * group (BRIEF §3.2): a section (24 above) → a header → the inset card (margin
 * 0 16, radius 22) → a 13px grey footer. Headers: 'notes' = Title 3 20/600 with a
 * grey count and an optional end label; 'caps' = 13px uppercase grey (Settings).
 */
export function group(rowsHtml, { header = '', headerStyle = 'notes', count = null, end = '', footer = '', footerHtml = '', flush = false, cls = '' } = {}) {
  const c = count != null && count !== '' ? `<span class="p4-gh-count">${esc(count)}</span>` : '';
  const h = !header ? '' : headerStyle === 'caps'
    ? `<div class="p4-gh caps"><span>${esc(header)}</span>${end ? `<span>${esc(end)}</span>` : ''}</div>`
    : `<div class="p4-gh"><h2>${esc(header)}${c}</h2>${end ? `<span class="p4-gh-end">${esc(end)}</span>` : ''}</div>`;
  const f = footerHtml || (footer ? esc(footer) : '');
  return `<section class="p4-group${flush ? ' flush' : ''}${cls ? ` ${esc(cls)}` : ''}">${h}<div class="p4-gcard">${rowsHtml}</div>${f ? `<p class="p4-gf">${f}</p>` : ''}</section>`;
}

/** Leads for a row (BRIEF §3.2). */
export const dotLead = (tone = 'tint', size = 9) => `<span class="p4-dot ${esc(tone)}${size !== 9 ? ` s${size}` : ''}" aria-hidden="true"></span>`;
export const avatarLead = (ini, { size = 36, me = false } = {}) => `<span class="p4-avl s${size}${me ? ' me' : ''}" aria-hidden="true">${esc(ini || '·')}</span>`;
/** A Settings-style 29px icon square. `colour` is a tone (tint, ok, late, att, ink3) or one of
 *  the fixed destination colours by name (track, indigo, red, blue, grey, orange, green,
 *  attention, teal, purple — tokens/mobile.css --m-sys-*; More's destinations, BRIEF §5.A). */
export const sym = (ic, colour = 'tint') => `<span class="p4-sym ${esc(colour)}" aria-hidden="true">${sf(ic, 18, 2)}</span>`;
export const monoNo = (n) => `<span class="p4-mono">${esc(n)}</span>`;
/** The tick (§3.10): a 24px circle, done = tint fill and a check; a button with a 44 hit. */
export const tickLead = (done, act, { label = '' } = {}) =>
  `<button type="button" class="p4-tk${done ? ' done' : ''}" data-act="${esc(act)}" data-tick aria-pressed="${!!done}" aria-label="${esc(label || (done ? 'Reopen' : 'Mark done'))}">${done ? sf('check', 14, 2.6) : ''}</button>`;
/** The iOS way to show a chosen option: a 20px tint tick at the row's trailing edge. */
export const checkmark = (on) => (on ? `<span class="p4-ck" aria-hidden="true">${sf('check', 20, 2.6)}</span>` : '<span class="p4-ck off" aria-hidden="true"></span>');

/**
 * row (BRIEF §3.2): Body 17 title (one line; `lines: 2` clamps two), Subhead 15
 * grey meta, an optional right-aligned 17 grey tabular value, the 18px chevron
 * when the row opens something (P1). `trailingHtml` is the row's one exceptional
 * control (Sign / Chase / Retry / Check in, a switch, a checkmark).
 * `swipe`: { lead: {label, act}, more: act, del: {label, act} } (§3.2 swipeRow).
 */
export function row({ title = '', titleHtml = '', sub = '', subHtml = '', value = '', leadHtml = '', trailingHtml = '', chevron: chev = null,
  late = false, subLate = false, valueLate = false, lines = 1, dim = false, done = false, act = '', attrs = '', swipe = null, cls = '' } = {}) {
  const opens = chev == null ? !!act && !trailingHtml : chev;
  const sw = swipe ? [
    swipe.lead ? ` data-swipe-lead="${esc(swipe.lead.act)}" data-swipe-lead-l="${esc(swipe.lead.label)}"` : '',
    swipe.more ? ` data-swipe-more="${esc(swipe.more)}"` : '',
    swipe.del ? ` data-swipe-del="${esc(swipe.del.act)}" data-swipe-del-l="${esc(swipe.del.label)}"` : '',
  ].join('') : '';
  const subBody = subHtml || (sub ? esc(sub) : '');
  const two = !!subBody || lines === 2;
  return `<div class="p4-row p4-cell${leadHtml ? ' lead' : ''}${two ? ' two' : ''}${dim ? ' dim' : ''}${done ? ' done' : ''}${cls ? ` ${esc(cls)}` : ''}"${act ? ` data-act="${esc(act)}" role="link"` : ''}${sw} ${attrs}>
    ${leadHtml}<div class="p4-cell-b"><div class="p4-cell-t${late ? ' late' : ''}${lines === 2 ? ' l2' : ''}">${titleHtml || esc(title)}</div>${subBody ? `<div class="p4-cell-s${subLate ? ' late' : ''}">${subBody}</div>` : ''}</div>
    ${value !== '' && value != null ? `<span class="p4-cell-v${valueLate ? ' late' : ''}">${esc(value)}</span>` : ''}${trailingHtml}${opens ? `<span class="p4-cell-chev" aria-hidden="true">${sf('chevron', 18, 2.2)}</span>` : ''}</div>`;
}

/** A single-choice list (BRIEF §3.5): rows with a checkmark; tapping one is the answer (P2). */
export const choiceGroup = (options, opts = {}) => group(options.map((o) => row({
  title: o.label, sub: o.sub || '', act: o.act, chevron: false, trailingHtml: checkmark(!!o.on), attrs: `aria-pressed="${!!o.on}"${o.style ? ` style="${esc(o.style)}"` : ''}`,
})).join(''), opts);

/**
 * button (BRIEF §3.4): a capsule (radius = height / 2), 17/600. prominent (the
 * screen's one main action), tinted, grey, plain, destructive. `height` 50 or 34
 * (a row's button). `href` makes it a real link (tel:, mailto:, https:).
 */
export function button(label, style = 'prominent', { act = '', full = true, icon: ic = '', height = 50, disabled = false, attrs = '', href = '' } = {}) {
  const cls = `p4-b ${esc(style)}${full ? ' full' : ''}${height === 34 ? ' h34' : ''}`;
  const inner = `${ic ? sf(ic, 20, 2) : ''}<span>${esc(label)}</span>`;
  if (href) return `<a class="${cls}" href="${esc(href)}"${/^https?:/i.test(href) ? ' target="_blank" rel="noopener"' : ''} ${attrs}>${inner}</a>`;
  return `<button type="button" class="${cls}" ${disabled ? 'aria-disabled="true"' : act ? `data-act="${esc(act)}"` : ''} ${attrs}>${inner}</button>`;
}
/** Two equal capsules with a 12 gap. */
export const buttonRow = (a, b) => `<div class="p4-brow">${a}${b}</div>`;
/** A flow's footer: buttons pinned at the bottom over a ground fade, a 13px grey note under. */
export const pinnedBar = (buttonsHtml, { note = '' } = {}) => `<div class="p4-pin">${buttonsHtml}${note ? `<p class="p4-pin-note">${esc(note)}</p>` : ''}</div>`;
/** iOS Contacts-style action tiles: 52px tint circles with 12px tint labels. items: [label, icon, act, disabled]. */
export const actionTiles = (items) => `<div class="p4-atiles">${items.map(([label, ic, act, off]) => `<button type="button" class="p4-atile"${off ? ' aria-disabled="true"' : ` data-act="${esc(act)}"`}><span class="c" aria-hidden="true">${sf(ic, 24, 1.9)}</span><span class="l">${esc(label)}</span></button>`).join('')}</div>`;

/** Chips (BRIEF §3.5): 34px capsules, the active one tint-filled. items: {key, label}. */
export const chips = (items, activeKey, actPrefix) => `<div class="p4-chipsd" role="tablist">${items.map((it) => `<button type="button" role="tab" class="p4-chipd${it.key === activeKey ? ' on' : ''}" aria-selected="${it.key === activeKey}" data-act="${esc(actPrefix)}:${esc(it.key)}">${esc(it.label)}</button>`).join('')}</div>`;

/** Figures (BRIEF §3.5): a card with a 13 grey label over a 28/700 tabular value and a sub line.
 *  `valueHtml` / `subHtml` are safe HTML (Money's frosted £, money-lock.js fz). */
export const figure = (value, label, { sub = '', late = false, wide = false, valueHtml = '', subHtml = '' } = {}) =>
  `<div class="p4-fg${wide ? ' wide' : ''}"><div class="l">${esc(label)}</div><div class="v">${valueHtml || esc(value)}</div>${subHtml || sub ? `<div class="s${late ? ' late' : ''}">${subHtml || esc(sub)}</div>` : ''}</div>`;
export const figures = (cardsHtml, { cols = 2 } = {}) => `<div class="p4-fgs c${cols}">${cardsHtml}</div>`;
/** A horizontal strip of small stat cards (Today: logged today in the tint, then the next events). */
export const statStrip = (items) => `<div class="p4-statd" role="list">${items.map((it) => {
  const inner = `<span class="k">${esc(it.k)}</span><span class="v">${esc(it.v)}</span>`;
  const cls = `p4-statd-c${it.tint ? ' tint' : ''}`;
  if (it.href) return `<a class="${cls}" role="listitem" href="${esc(it.href)}" target="_blank" rel="noopener">${inner}</a>`;
  return it.act ? `<button type="button" role="listitem" class="${cls}" data-act="${esc(it.act)}">${inner}</button>` : `<div role="listitem" class="${cls}">${inner}</div>`;
}).join('')}</div>`;
/** Key–value lines in a group (Details, Chasing). value is text; `valueHtml` safe HTML. */
export const kv = (rows, opts = {}) => group(rows.map((r) => `<div class="p4-row p4-kvd"><span class="k">${esc(r[0])}</span><span class="v">${r.valueHtml || esc(r[1])}</span></div>`).join(''), opts);

/** A text field (BRIEF §3.5): a 13px caps label, a 44px card (120 multiline), a help line. */
export function textField(label, placeholder, { value = '', multiline = false, help = '', attrs = '' } = {}) {
  const ctl = multiline ? `<textarea class="p4-tf-in" placeholder="${esc(placeholder)}" aria-label="${esc(label || placeholder)}" ${attrs}>${esc(value)}</textarea>`
    : `<input class="p4-tf-in" value="${esc(value)}" placeholder="${esc(placeholder)}" aria-label="${esc(label || placeholder)}" ${attrs}>`;
  return `<div class="p4-tf${multiline ? ' multi' : ''}">${label ? `<div class="p4-tf-l">${esc(label)}</div>` : ''}<label class="p4-tf-card">${ctl}</label>${help ? `<p class="p4-tf-h">${esc(help)}</p>` : ''}</div>`;
}

/** Quiet sync (P6, BRIEF §3.7): a 28px grey capsule centred under the header, said once. */
export const statusCapsule = (text, ic = 'cloud-check') => `<div class="p4-capsule-row" role="status"><span class="p4-capsule">${sf(ic, 16, 1.8)}${esc(text)}</span></div>`;

/** Loading (BRIEF §5.G States): three skeleton rows inside a group — never a spinner. */
export const skeletonGroup = (n = 3, opts = {}) => group(Array.from({ length: n }, () => '<div class="p4-row p4-skelrow" aria-hidden="true"><i></i><i></i></div>').join(''), opts);
/** Empty: a group holding a centred Headline and Subhead — never a dashed box. */
export const emptyGroup = (title, body = '', opts = {}) => group(`<div class="p4-emptyd"><div class="t">${esc(title)}</div>${body ? `<div class="b">${esc(body)}</div>` : ''}</div>`, opts);

/* ── Layers: menus and alerts (BRIEF §3.7) ──────────────────────────────────
 * Drawn into their own host beside the app root, so a repaint of the screen
 * never takes them away; each takes a history entry through the shell's layer
 * stack (registerLayerHooks), so the back gesture closes it first (P8). */
let layerHooks = { push: () => {}, pop: () => {}, dispatch: () => {} };
export const registerLayerHooks = (h) => { layerHooks = { ...layerHooks, ...h }; };
function layerHost() {
  let h = document.getElementById('p4-layers');
  if (!h) { h = document.createElement('div'); h.id = 'p4-layers'; h.className = 'm-app p4-layers'; document.body.appendChild(h); }
  return h;
}
let openLayer = null;
function closeLayer(fromHistory = false) {
  if (!openLayer) return Promise.resolve();
  const L = openLayer;
  openLayer = null;
  const el = L.el;
  el.classList.add('closing');
  setTimeout(() => el.remove(), reducedMotion() ? 0 : 180);
  return fromHistory ? Promise.resolve() : Promise.resolve(layerHooks.pop());
}
const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
function mountLayer(html, onPick) {
  closeLayer();
  const el = document.createElement('div');
  el.className = 'p4-layer';
  el.innerHTML = html;
  layerHost().appendChild(el);
  openLayer = { el };
  layerHooks.push(() => closeLayer(true));
  el.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-pick]');
    if (b) { ev.preventDefault(); const k = b.dataset.pick; closeLayer().then(() => onPick(k)); return; }
    if (ev.target.closest('[data-layer-scrim]') || ev.target === el) closeLayer();
  });
  return el;
}

/**
 * menu (BRIEF §3.7): a pull-down from a ··· button (or a long-press): 250 wide,
 * radius 14, glass, 44px rows 17/400 with a trailing 20px glyph; `gap: true` puts
 * an 8px grey gap after a row; destructive rows in --m-destructive. Each item's
 * `act` is dispatched like a tap on [data-act].
 */
export function openMenu(anchor, items, { dim = false } = {}) {
  const r = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : { right: innerWidth - 16, bottom: 100 };
  const top = Math.min(r.bottom + 6, innerHeight - 44 * items.length - 40);
  const right = Math.max(16, innerWidth - r.right);
  // `dim`: the screen behind darkens under the menu (board Project-menu); otherwise the scrim is clear.
  const html = `<div class="p4-layer-scrim${dim ? '' : ' clear'}" data-layer-scrim></div>
    <div class="p4-menu" role="menu" style="top:${Math.round(top)}px;right:${Math.round(right)}px">${/* data: anchored to the button */''}
    ${items.map((it, i) => `<button type="button" role="menuitem" class="p4-menu-i${it.tone === 'destructive' ? ' destructive' : ''}${it.gap ? ' gap' : ''}" data-pick="${i}"><span>${esc(it.label)}</span>${it.icon ? sf(it.icon, 20, 1.8) : ''}</button>`).join('')}</div>`;
  mountLayer(html, (k) => { const it = items[Number(k)]; if (it && it.act) layerHooks.dispatch(it.act); else if (it && it.run) it.run(); });
}

/**
 * alert (BRIEF §3.7): the 270px centred iOS alert, only for an irreversible step
 * (Sign and issue, Retire, Submit week, Check out). Two actions, the confirming
 * one bold (or destructive). actions: [{label, act|run, bold, destructive}].
 */
export function openAlert(title, message, actions) {
  const html = `<div class="p4-layer-scrim" data-layer-scrim></div>
    <div class="p4-alertd" role="alertdialog" aria-modal="true" aria-label="${esc(title)}">
      <div class="p4-alertd-b"><div class="t">${esc(title)}</div>${message ? `<div class="m">${esc(message)}</div>` : ''}</div>
      <div class="p4-alertd-a${actions.length > 2 ? ' stack' : ''}">${actions.map((a, i) => `<button type="button" class="${a.bold ? 'bold' : ''}${a.destructive ? ' destructive' : ''}" data-pick="${i}">${esc(a.label)}</button>`).join('')}</div></div>`;
  mountLayer(html, (k) => { const a = actions[Number(k)]; if (a && a.act) layerHooks.dispatch(a.act); else if (a && a.run) a.run(); });
}
/** auth.js's question before sign-out loses queued work, as the phone asks it. Stay is the bold one. */
export const phoneConfirmLoss = ({ title, body, stay, go }) => new Promise((resolve) => {
  openAlert(title, body, [
    { label: stay, bold: true, run: () => resolve(false) },
    { label: go, destructive: true, run: () => resolve(true) },
  ]);
});
export const closeLayers = () => closeLayer();

/**
 * swipeRow (BRIEF §3.2): pointer events, a 44px threshold, spring back. Leading
 * (a right swipe) runs the row's one-tap verb over green; trailing (a left swipe)
 * opens More and, where it exists, the destructive verb. The tap a swipe ends
 * with never opens the row.
 */
export function swipeRow(el, { leading = null, trailing = [] } = {}, run = (act) => layerHooks.dispatch(act)) {
  if (!el || el.dataset.swipeWired) return;
  el.dataset.swipeWired = '1';
  let x0 = 0; let y0 = 0; let dx = 0; let live = false; let id = null; let open = false;
  const W = 74 * trailing.length;
  const wrap = () => {
    if (el.querySelector('.p4-swipe-under')) return;
    const u = document.createElement('div');
    u.className = 'p4-swipe-under';
    u.setAttribute('aria-hidden', 'true');
    u.innerHTML = `${leading ? `<span class="lead"><b>${esc(leading.label)}</b></span>` : ''}<span class="trail">${trailing.map((t, i) => `<button type="button" class="${t.tone === 'destructive' ? 'del' : 'more'}" data-swipe-i="${i}" tabindex="-1">${esc(t.label)}</button>`).join('')}</span>`;
    el.prepend(u);
    u.addEventListener('click', (ev) => {
      const b = ev.target.closest('[data-swipe-i]');
      ev.stopPropagation(); ev.preventDefault();
      if (!b) return;
      settle(0); run(trailing[Number(b.dataset.swipeI)].act);
    });
  };
  const body = () => el.querySelector('.p4-swipe-body') || el;
  const settle = (to) => { open = to !== 0; el.classList.toggle('swipe-open', open); el.style.setProperty('--sx', `${to}px`); el.classList.remove('swiping'); };
  el.addEventListener('pointerdown', (ev) => {
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;
    x0 = ev.clientX; y0 = ev.clientY; dx = 0; live = false; id = ev.pointerId;
  });
  el.addEventListener('pointermove', (ev) => {
    if (id !== ev.pointerId) return;
    const mx = ev.clientX - x0; const my = ev.clientY - y0;
    if (!live) {
      if (Math.abs(mx) < 10 || Math.abs(mx) < Math.abs(my) * 1.4) { if (Math.abs(my) > 12) id = null; return; }
      if ((mx > 0 && !leading && !open) || (mx < 0 && !trailing.length)) { id = null; return; }
      live = true; wrap(); el.classList.add('swiping');
      try { el.setPointerCapture(ev.pointerId); } catch (_) { /* fine */ }
    }
    dx = (open ? -W : 0) + mx;
    const lim = leading ? 160 : 0;
    el.style.setProperty('--sx', `${Math.max(-W - 40, Math.min(lim, dx))}px`);
    el.classList.toggle('swipe-lead', dx > 0);
  });
  const end = (ev) => {
    if (id !== ev.pointerId) return;
    id = null;
    if (!live) return;
    live = false;
    swallowClick(el);
    if (dx >= 44 && leading) { settle(0); run(leading.act); return; }
    if (dx <= -44 && trailing.length) { settle(-W); return; }
    settle(0);
  };
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', (ev) => { if (id === ev.pointerId) { id = null; live = false; settle(0); } });
  void body;
}
function swallowClick(el) {
  const stop = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
  el.addEventListener('click', stop, { capture: true, once: true });
  setTimeout(() => el.removeEventListener('click', stop, { capture: true }), 400);
}
/** Wire every row a screen marked with data-swipe-lead / -more / -del (after each paint). */
export function wireSwipes(root) {
  root.querySelectorAll('[data-swipe-lead], [data-swipe-more], [data-swipe-del]').forEach((el) => {
    const d = el.dataset;
    const trailing = [];
    if (d.swipeMore) trailing.push({ label: 'More', act: d.swipeMore });
    if (d.swipeDel) trailing.push({ label: d.swipeDelL || 'Delete', act: d.swipeDel, tone: 'destructive' });
    swipeRow(el, { leading: d.swipeLead ? { label: d.swipeLeadL || 'Done', act: d.swipeLead } : null, trailing });
  });
}

/* ── Release D · PR 6 (Assets, BRIEF §5.C) — additions ─────────────────────── */
/** A numbered step lead (Write tag on an iPhone): a 28px tint-tinted circle with the number. */
export const stepLead = (n) => `<span class="p4-stepno" aria-hidden="true">${esc(n)}</span>`;

/* ── Release D PR 2 (Project, BRIEF §5.B): the pieces the project's screens add ── */

/** section_title (lib.py): a Title 3 head with an optional grey end label, no card under it (Money, Photos). */
export const sectionTitle = (text, { end = '' } = {}) =>
  `<div class="p4-st"><h2>${esc(text)}</h2>${end !== '' && end != null ? `<span class="p4-gh-end">${esc(end)}</span>` : ''}</div>`;

/** pill (lib.py pill): 22px, 13 (the weight is .p4-pill's own) — tone grey · ok · late · tint.
 *  `act` makes it a button with a 44 hit (Project's stage pill). The one pill: PR 7 folded
 *  PR 2's pillD, PR 6's tonePill and PR 4's work-ui pill into it. */
export const pill = (label, tone = 'grey', { act = '' } = {}) => (act
  ? `<button type="button" class="p4-pill d ${esc(tone)}" data-act="${esc(act)}">${esc(label)}</button>`
  : `<span class="p4-pill d ${esc(tone)}">${esc(label)}</span>`);

/** big_hero (lib.py): a 13 grey eyebrow, the Title 1, then a 15 grey line (safe HTML: pills, a late part).
 *  The one hero: PR 7 folded PR 6's heroBlock into it. */
export const hero = (title, { eyebrow = '', subHtml = '' } = {}) =>
  `<div class="p4-hero">${eyebrow ? `<div class="e">${esc(eyebrow)}</div>` : ''}<h1>${esc(title)}</h1>${subHtml ? `<div class="s">${subHtml}</div>` : ''}</div>`;

/** stepper_time (BRIEF §3.5): two 56px fill circles either side of a 56/700 tabular value. */
export const stepperTime = (value, { minusAct, plusAct, hint = 'hours · 15 min steps', minusLabel = 'Less', plusLabel = 'More' } = {}) =>
  `<div class="p4-stepper"><button type="button" class="c" data-act="${esc(minusAct)}" aria-label="${esc(minusLabel)}">${sf('minus', 24, 1.8)}</button>
    <div class="m"><div class="v">${esc(value)}</div>${hint ? `<div class="h">${esc(hint)}</div>` : ''}</div>
    <button type="button" class="c" data-act="${esc(plusAct)}" aria-label="${esc(plusLabel)}">${sf('plus', 24, 1.8)}</button></div>`;

/**
 * photo_tiles (lib.py): a strip of 100px tiles, the tint "Take photo" first. tiles: [{ url, act?, dropAct? }]
 * — the url is an object URL or data URL already made safe by the caller (blob:/data:).
 */
export const photoTiles = (tiles, { takeAct = '', takeLabel = 'Take photo', size = 100 } = {}) => `<div class="p4-ptiles${size !== 100 ? ` s${size}` : ''}">
  ${takeAct ? `<button type="button" class="p4-ptile take" data-act="${esc(takeAct)}">${sf('camera', 26, 1.8)}<span>${esc(takeLabel)}</span></button>` : ''}
  ${tiles.map((t) => `<div class="p4-ptile" role="img" aria-label="Photo" style="background-image:url('${esc(t.url)}')">${/* data: the photo */''}${t.dropAct ? `<button type="button" class="x" data-act="${esc(t.dropAct)}" aria-label="Remove this photo">${sf('xmark', 14, 2.4)}</button>` : ''}</div>`).join('')}</div>`;

/* ── Release D PR 4 (Work, BRIEF §3.5 / §5.D) — folded in from mobile/work-ui.js by PR 7 ──
 * The day strip (Time), the week strip and the event lead (Calendar), link tiles and
 * link circles (Client, Person, Enquiry: tel: / mailto: / sms: / Maps are real links,
 * never buttons that fake one; every href goes through phone-model.js safeLink). */
/** dayStrip (BRIEF §3.5, lib.py day_strip): 60px day cards, the chosen one tint-filled.
 *  days: [{ key, label, value, act, on, leave }] */
export const dayStrip = (days) => `<div class="p4-wdays" role="tablist">${days.map((d) => `<button type="button" role="tab" class="p4-wday${d.on ? ' on' : ''}${d.leave ? ' leave' : ''}" data-act="${esc(d.act)}" aria-selected="${!!d.on}"><span class="d">${esc(d.label)}</span><span class="v">${esc(d.value)}</span></button>`).join('')}</div>`;

/** The calendar's 7-day strip (board Calendar): the weekday over a 36px circle (the chosen
 *  day tint-filled, today in the tint), a 5px dot under a day that has something.
 *  days: [{ key, wd, n, on, today, dot, act, label }] */
export const weekStrip = (days) => `<div class="p4-wweek" role="tablist">${days.map((d) => `<button type="button" role="tab" class="p4-wwd${d.on ? ' on' : ''}${d.today ? ' today' : ''}" data-act="${esc(d.act)}" aria-selected="${!!d.on}" aria-label="${esc(d.label)}"><span class="wd">${esc(d.wd)}</span><span class="n">${esc(d.n)}</span><i class="${d.dot ? 'on' : ''}" aria-hidden="true"></i></button>`).join('')}</div>`;

/** A calendar row's lead: the time (or "All day") over 44px, then a 3px bar in the event's tone (ok · tint · grey). */
export const evLead = (time, tone = 'grey') => `<span class="p4-wev" aria-hidden="true"><span class="t">${esc(time)}</span><i class="${esc(tone)}"></i></span>`;

/** Action tiles that are real links (iOS Contacts): [label, icon, href]. A tile with no
 *  usable link is drawn disabled, never dead-but-live. */
export const linkTiles = (items) => `<div class="p4-atiles">${items.map(([label, ic, href]) => {
  const h = safeLink(href || '');
  const inner = `<span class="c" aria-hidden="true">${sf(ic, 24, 1.9)}</span><span class="l">${esc(label)}</span>`;
  return h ? `<a class="p4-atile" href="${esc(h)}"${/^https?:/i.test(h) ? ' target="_blank" rel="noopener"' : ''}>${inner}</a>`
    : `<span class="p4-atile" aria-disabled="true">${inner}</span>`;
}).join('')}</div>`;

/** A 36px tint circle that is a link (Enquiry's Who row: call, email). Nothing to link → nothing drawn. */
export const linkCircle = (ic, label, href) => {
  const h = safeLink(href || '');
  return h ? `<a class="p4-wcirc" href="${esc(h)}" aria-label="${esc(label)}"${/^https?:/i.test(h) ? ' target="_blank" rel="noopener"' : ''}>${sf(ic, 18, 2)}</a>` : '';
};

/** A Directions link for an address (Apple Maps opens it on an iPhone, Google Maps elsewhere). */
export const mapsHref = (address) => {
  const a = String(address || '').trim();
  return a ? `https://maps.apple.com/?q=${encodeURIComponent(a)}` : '';
};
